﻿import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  EnvelopeIcon,
  LockClosedIcon,
  BuildingOfficeIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  MagnifyingGlassIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import { API_ORIGIN } from '../config/api'
import { TurnstileWidget, type TurnstileHandle } from '../components/TurnstileWidget';

interface Organization {
  id: string;
  name: string;
  subdomain: string;
}

type AuthTab = 'login' | 'forgot' | 'reset';

export function Auth() {
  const [tab, setTab] = useState<AuthTab>('login');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login, requestPasswordReset, resetPassword, isAuthenticated } = useAuth();

  // Configurable tenant-admin signup URL from env.
  //
  // The previous fallback was `http://localhost:5173/login`, which shipped to
  // production: the deployed connector's "Go to Tenant Admin" button sent real
  // users to a dead localhost address. The default now points at the live portal.
  const tenantAdminSignupUrl =
    import.meta.env.VITE_TENANT_ADMIN_SIGNUP_URL ||
    `${import.meta.env.VITE_TENANT_ADMIN_URL || 'https://app.retailsync.africa'}/login`;

  // Organization search state
  const [orgSearchQuery, setOrgSearchQuery] = useState('');
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [isOrgDropdownOpen, setIsOrgDropdownOpen] = useState(false);
  const [isSearchingOrg, setIsSearchingOrg] = useState(false);
  const orgDropdownRef = useRef<HTMLDivElement>(null!);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Check if reset token is in URL
  useEffect(() => {
    const resetToken = searchParams.get('reset_token');
    if (resetToken) {
      setTab('reset');
    }
  }, [searchParams]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (orgDropdownRef.current && !orgDropdownRef.current.contains(event.target as Node)) {
        setIsOrgDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search organizations with debounce
  const searchOrganizations = useCallback(async (query: string) => {
    if (query.length < 2) {
      setOrganizations([]);
      setIsOrgDropdownOpen(false);
      return;
    }

    setIsSearchingOrg(true);
    try {
      const rawApiBaseUrl = API_ORIGIN
        .replace(/\/+$/, '')
        .replace(/\/api\/v1$/i, '');
      const url = `${rawApiBaseUrl}/api/v1/auth/organizations/search?q=${encodeURIComponent(query)}`;

      const response = await fetch(url, {
        headers: { 'x-client-type': 'connector-service' },
      });

      if (response.ok) {
        const result = await response.json() as { data: Organization[] };
        setOrganizations(result.data || []);
        setIsOrgDropdownOpen(true);
      }
    } catch (err) {
      console.error('Organization search failed:', err);
    } finally {
      setIsSearchingOrg(false);
    }
  }, []);

  const handleOrgSearchChange = (value: string) => {
    setOrgSearchQuery(value);
    setSelectedOrg(null);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchOrganizations(value);
    }, 300);
  };

  const handleSelectOrg = (org: Organization) => {
    setSelectedOrg(org);
    setOrgSearchQuery(org.name);
    setIsOrgDropdownOpen(false);
    setOrganizations([]);
  };

  const handleClearOrg = () => {
    setSelectedOrg(null);
    setOrgSearchQuery('');
    setOrganizations([]);
  };

  // LOGIN TAB
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // The challenge token is single-use, so the widget is reset after every attempt
  // (success or failure) to issue a fresh one before a retry. Mirrors
  // tenant-admin/src/pages/Auth.tsx — both apps hit the same login endpoint.
  const loginTurnstileRef = useRef<TurnstileHandle>(null);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');

    const turnstileToken = loginTurnstileRef.current?.getToken();
    if (!turnstileToken) {
      setError('Please complete the verification challenge.');
      return;
    }

    setIsLoading(true);

    try {
      await login(
        loginEmail,
        loginPassword,
        selectedOrg?.subdomain,
        selectedOrg?.id,
        turnstileToken
      );
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      // Tokens are single-use — issue a fresh challenge before any retry.
      loginTurnstileRef.current?.reset();
      setIsLoading(false);
    }
  };

  // FORGOT PASSWORD TAB
  const [forgotEmail, setForgotEmail] = useState('');

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');
    setIsLoading(true);

    try {
      const result = await requestPasswordReset(forgotEmail);
      setSuccessMsg(result.message || 'Password reset link sent to your email');
      setForgotEmail('');
    } catch (err: any) {
      setError(err.message || 'Failed to send reset email');
    } finally {
      setIsLoading(false);
    }
  };

  // RESET PASSWORD TAB
  const [resetToken, setResetToken] = useState(searchParams.get('reset_token') || '');
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');

    if (resetPasswordValue !== resetConfirm) {
      setError('Passwords do not match');
      return;
    }

    if (resetPasswordValue.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);
    try {
      const result = await resetPassword(resetToken, resetPasswordValue);
      setSuccessMsg(result.message || 'Password updated! Redirecting to login...');
      setResetToken('');
      setResetPasswordValue('');
      setResetConfirm('');

      setTimeout(() => {
        setTab('login');
        setSuccessMsg('');
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 via-white to-blue-50 px-4">
      {/* Top branding */}
      <div className="mb-8 text-center">
        <h1 className="text-4xl font-bold text-gray-900">Universal Connector</h1>
        <p className="mt-2 text-lg text-gray-600">RetailsyncGo Import Service</p>
      </div>

      {/* Auth card */}
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          {(['login', 'forgot'] as const).map((tabName) => (
            <button
              key={tabName}
              onClick={() => {
                setTab(tabName);
                setError('');
                setSuccessMsg('');
              }}
              className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                tab === tabName
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tabName === 'login' && 'Sign In'}
              {tabName === 'forgot' && 'Forgot?'}
            </button>
          ))}
        </div>

        <div className="p-8 space-y-6">
          {/* Alerts */}
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-4 flex gap-3">
              <ExclamationCircleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          {successMsg && (
            <div className="rounded-lg bg-green-50 border border-green-200 p-4 flex gap-3">
              <CheckCircleIcon className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-green-700">{successMsg}</p>
            </div>
          )}

          {/* LOGIN FORM */}
          {tab === 'login' && (
            <form onSubmit={handleLogin} className="space-y-4">
              {/* Organization Search */}
              <div ref={orgDropdownRef} className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Organization {selectedOrg && <span className="text-green-600 text-xs">âœ“ Selected</span>}
                </label>
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={orgSearchQuery}
                    onChange={(e) => handleOrgSearchChange(e.target.value)}
                    onFocus={() => {
                      if (organizations.length > 0) setIsOrgDropdownOpen(true);
                    }}
                    placeholder="Search your organization..."
                    className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {selectedOrg && (
                    <button
                      type="button"
                      onClick={handleClearOrg}
                      className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                    >
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                  {isSearchingOrg && (
                    <div className="absolute right-3 top-3">
                      <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                  )}
                </div>

                {/* Dropdown */}
                {isOrgDropdownOpen && organizations.length > 0 && (
                  <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {organizations.map((org) => (
                      <button
                        key={org.id}
                        type="button"
                        onClick={() => handleSelectOrg(org)}
                        className={`w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors flex items-center gap-3 ${
                          selectedOrg?.id === org.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                        }`}
                      >
                        <BuildingOfficeIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{org.name}</p>
                          <p className="text-xs text-gray-500">{org.subdomain}.retailsync.africa</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {isOrgDropdownOpen && organizations.length === 0 && orgSearchQuery.length >= 2 && !isSearchingOrg && (
                  <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center">
                    <p className="text-sm text-gray-500">No organizations found</p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <div className="relative">
                  <EnvelopeIcon className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                  <input
                    type="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <div className="relative">
                  <LockClosedIcon className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                  <input
                    type="password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    placeholder="â€¢â€¢â€¢â€¢â€¢â€¢â€¢â€¢"
                    required
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              {/* Cloudflare Turnstile — REQUIRED by POST /auth/login for any
                  non-device client. `x-client-type: connector-service` is not in
                  the API's device list (pos/mobile), so without this every login
                  is refused with TURNSTILE_FAILED before the password is checked. */}
              <TurnstileWidget ref={loginTurnstileRef} action="staff_login" />
              <button
                type="submit"
                disabled={isLoading || !selectedOrg}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-2.5 rounded-lg transition-colors"
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
                <ArrowRightIcon className="h-4 w-4" />
              </button>
            </form>
          )}

          {/* FORGOT PASSWORD FORM */}
          {tab === 'forgot' && (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <p className="text-sm text-gray-600">Enter your email to receive a password reset link</p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <div className="relative">
                  <EnvelopeIcon className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                  <input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white font-medium py-2.5 rounded-lg transition-colors"
              >
                {isLoading ? 'Sending...' : 'Send Reset Link'}
                <ArrowRightIcon className="h-4 w-4" />
              </button>
            </form>
          )}

          {/* RESET PASSWORD FORM */}
          {tab === 'reset' && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                <div className="relative">
                  <LockClosedIcon className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                  <input
                    type="password"
                    value={resetPasswordValue}
                    onChange={(e) => setResetPasswordValue(e.target.value)}
                    placeholder="â€¢â€¢â€¢â€¢â€¢â€¢â€¢â€¢"
                    required
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">Minimum 8 characters</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password</label>
                <div className="relative">
                  <LockClosedIcon className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                  <input
                    type="password"
                    value={resetConfirm}
                    onChange={(e) => setResetConfirm(e.target.value)}
                    placeholder="â€¢â€¢â€¢â€¢â€¢â€¢â€¢â€¢"
                    required
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-2.5 rounded-lg transition-colors"
              >
                {isLoading ? 'Updating...' : 'Update Password'}
                <ArrowRightIcon className="h-4 w-4" />
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Signup info â€” no signup screen, redirect to tenant-admin */}
      <div className="mt-6 w-full max-w-md bg-blue-50 border border-blue-200 rounded-2xl p-5 text-center">
        <div className="flex items-center justify-center gap-2 mb-2">
          <BuildingOfficeIcon className="h-5 w-5 text-blue-600" />
          <h3 className="text-sm font-semibold text-blue-900">Don&apos;t have an account?</h3>
        </div>
        <p className="text-sm text-blue-700 mb-3">
          The Universal Connector shares accounts with the RetailsyncGo Tenant Admin portal.
          Sign up there first, then return here to import your data.
        </p>
        <a
          href={tenantAdminSignupUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
        >
          Go to Tenant Admin
          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
        </a>
      </div>

      {/* Footer info */}
      <p className="mt-4 text-center text-sm text-gray-500 max-w-md">
        Import data from any POS system into your RetailsyncGo tenant.
      </p>
    </div>
  );
}
